import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Paintbrush, Home, Grid3x3, Droplet, Triangle, Sparkles, Hammer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { companyInfo, services } from '../mock';

const iconMap = {
  paintbrush: Paintbrush,
  home: Home,
  grid3x3: Grid3x3,
  droplet: Droplet,
  triangle: Triangle,
  sparkles: Sparkles,
  hammer: Hammer
};

export const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {companyInfo.tagline}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact">
                <Button size="lg" className="bg-gray-800 hover:bg-gray-700 text-white w-full sm:w-auto">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-50 w-full sm:w-auto">
                  Nous contacter
                </Button>
              </Link>
            </div>
            
            {/* Motto Banner */}
            <div className="bg-gray-800 text-white py-6 px-8 rounded-lg shadow-lg">
              <p className="text-2xl md:text-3xl font-bold">
                {companyInfo.motto}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nos Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Une gamme complète de services pour tous vos projets de rénovation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-gray-700" size={24} />
                    </div>
                    <CardTitle className="text-xl text-gray-900 mb-2">{service.title}</CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" size="lg" className="border-gray-300 text-gray-800 hover:bg-gray-50">
                Voir tous les services
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pourquoi nous choisir ?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {companyInfo.values.map((value, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="text-gray-700 flex-shrink-0 mt-1" size={24} />
                <p className="text-lg text-gray-700">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Contactez-nous dès aujourd'hui pour un devis gratuit et sans engagement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-gray-800 hover:bg-gray-700 text-white w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-gray-300 text-gray-800 hover:bg-gray-50 w-full sm:w-auto">
                Appeler maintenant
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};